import React, { useMemo } from 'react';
import { Alert, Empty, Spin } from 'antd';
import AreaMap from '../Map/AreaMap';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { useMeasurementReport } from './measurementReport/MeasurementReportContext';
import { MeasurementSectionShell, buildMeasurementSectionMeta } from './measurementReport/MeasurementSectionShell';

interface CommonSectionProps {
    section: any;
}

export const ReportMeasurementLocationMap: React.FC<CommonSectionProps> = ({ section }) => {
    const {
        measurementId,
        measurement,
        loading,
        areaGeometry,
        areaGeometryLoading,
        areaType,
        areaHierarchy,
        patternPoints,
    } = useMeasurementReport();
    const sectionTitle = section?.title || 'Location';
    const sectionMeta = buildMeasurementSectionMeta(measurement);

    const markerPoint = useMemo(() => {
        if (!measurement) return [];
        const geom = measurement.geom;
        if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
            const [lng, lat] = geom.coordinates as [number, number];
            return [{
                id: measurement.id,
                lat,
                lng,
                label: measurement.measurementCode,
                selected: true,
            }];
        }
        return [];
    }, [measurement]);

    const templateImageUrl = useMemo(() => {
        if (areaType?.id && (areaType as any).templateImage) {
            return `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-image?t=${Date.now()}`;
        }
        return null;
    }, [areaType]);

    const hierarchyAreas = useMemo(
        () => areaHierarchy.map((area: any, hierarchyDepth: number) => ({
            id: area.id,
            geom: area.geom,
            name: area.name,
            areaType: area.areaType || null,
            hierarchyDepth,
        })),
        [areaHierarchy],
    );

    const patternPointMarkers = useMemo(
        () => (patternPoints || [])
            .filter((point: any) => Number.isFinite(Number(point.gpsLat)) && Number.isFinite(Number(point.gpsLon)))
            .map((point: any) => ({
                id: point.id,
                lat: Number(point.gpsLat),
                lng: Number(point.gpsLon),
                label: point.label || `#${point.pointIndex}`,
                selected: point.id === measurement?.patternPoint?.id,
            })),
        [measurement?.patternPoint?.id, patternPoints],
    );

    const hasAreaGeometry = Boolean(areaGeometry) || hierarchyAreas.some((area: any) => Boolean(area.geom));

    if (!measurementId) {
        return (
            <MeasurementSectionShell title={sectionTitle}>
                <Alert message="Select a measurement to view location." type="info" showIcon />
            </MeasurementSectionShell>
        );
    }

    if (loading || areaGeometryLoading) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Spin tip="Loading location..." />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (!measurement) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <Alert message="Measurement not found" type="error" showIcon />
            </MeasurementSectionShell>
        );
    }

    if (markerPoint.length === 0 && !hasAreaGeometry) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Empty description="No GPS location set for this measurement" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            </MeasurementSectionShell>
        );
    }

    return (
        <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
            <div className="report-measurement-map-stage">
                <AreaMap
                    mapKey={`measurement-report-${measurement.id}`}
                    areaGeometry={hierarchyAreas.length > 0 ? null : (areaGeometry || null)}
                    areaName={measurement.area?.name}
                    measurements={markerPoint}
                    points={patternPointMarkers}
                    allAreas={hierarchyAreas}
                    areaType={measurement.area?.areaType || null}
                    templateImageUrl={templateImageUrl}
                    initialViewState={markerPoint.length > 0 ? {
                        longitude: markerPoint[0].lng,
                        latitude: markerPoint[0].lat,
                        zoom: 17,
                    } : undefined}
                    disableAutoFit={markerPoint.length > 0}
                />
            </div>
        </MeasurementSectionShell>
    );
};

export default ReportMeasurementLocationMap;
